import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchPositions, Position } from "services/positions.service";

export interface UsePositionsCatalogueResult {
  /** Every position including archived ones — needed to label an old selection. */
  positions: Position[];
  /** What a picker may offer. */
  active: Position[];
  byId: Map<string, Position>;
  loading: boolean;
  error: boolean;
  reload: () => void;
  /** Puts a just-created position into the list without a round trip. */
  add: (position: Position) => void;
}

/**
 * The company's catalogue, archived rows included (one request serves both
 * the options and the labels of a selection that points at an archived or
 * merged position). Only the latest request may write, so a company switch
 * mid-flight never shows the previous company's catalogue.
 */
export function usePositionsCatalogue(companyId: string | undefined): UsePositionsCatalogueResult {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    if (!companyId) {
      setPositions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    fetchPositions(companyId, { includeArchived: true })
      .then((rows) => {
        if (requestId !== requestRef.current) return;
        setPositions(rows);
      })
      .catch(() => {
        if (requestId !== requestRef.current) return;
        setPositions([]);
        setError(true);
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [companyId, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  const add = useCallback((position: Position) => {
    setPositions((prev) =>
      prev.some((row) => row.id === position.id) ? prev : [...prev, position]
    );
  }, []);

  const active = useMemo(
    () =>
      positions
        .filter((position) => !position.isArchived)
        .sort((a, b) => a.nameLT.localeCompare(b.nameLT, "lt")),
    [positions]
  );

  const byId = useMemo(
    () => new Map(positions.map((position) => [position.id, position])),
    [positions]
  );

  return { positions, active, byId, loading, error, reload, add };
}
